import type { ReactNode } from "react";
import { Box } from "../../components/ui/box";
import { Icon } from "../../components/ui/icon";
import type { Omittable } from "../../lib/props";
import { useWidgetBodyLoadingWhile } from "./contexts";
import { WidgetInteractive } from "./widget-interactive";

/**
 * The row's geometry, shared with its placeholder twin so the two cannot
 * drift: the wash bleeds `sm` past the content edge and pads the same `sm`
 * back, the caret leads with an `sm` gap, and the row pads `sm` above and
 * below its content.
 */
const ROW_INSET = "sm" as const;
/** The gap between a row's cells — shared with {@link WidgetListHeader} so
 *  header cells sit over row cells. */
export const ROW_GAP = "sm" as const;
/** The caret's box, in px — `Icon size="xs"` — held open by the placeholder
 *  so the content that follows it lands on the same edge as a real row's. */
/** The caret glyph's footprint (Icon xs), the slot a header leaves empty. */
export const CARET_PX = 14;

/**
 * The one widget body row that OPENS something — a member, a bucket, a stat
 * whose detail panel renders directly beneath it.
 *
 * It is `WidgetListRow`'s geometry (leading content on the card's content edge,
 * level with the header title, the wash bleeding `sm` past that edge on both
 * sides so the row is cushioned without spending inset) with the two things a
 * disclosure needs and a list row must not have:
 *
 * - **It IS the button.** A list row hosts real buttons in a trailing rail, so
 *   it can never be one. A disclosure row's whole line is the target — its
 *   content is a label rather than a set of controls — which is what lets the
 *   caret, the name, and the figures all open the same panel. That also means
 *   nothing interactive may go inside it: a button cannot nest in a button.
 * - **The caret is the row's, not the caller's.** A filled `caret-*`, never a
 *   `chevron-*`: the filled triangle is the disclosure mark everywhere here.
 *   Every disclosure reads the same way round, and the state it points at is
 *   the one this row already holds. A caller drawing its own caret is how two bodies on one board end
 *   up rotating opposite ways.
 *
 * `background="selected"` while open, so the open row and its panel read as one
 * object. Rows carry NO divider: the wash and the cushion are the separation,
 * and a rule under a rounded wash is the double framing `StatBand` documents.
 *
 * The row does NOT justify its content — the caret leads, and a trailing rail
 * of figures is a `fill="flex"` child taking the space between them. A caller
 * with a label alone then keeps the label against the caret rather than
 * having it pushed to the far edge.
 *
 * The bleed is a WRAPPER's, not the button's, and that is forced rather than
 * chosen: a `<button>` sizes to its content even as a block-level flex box, so
 * it needs `fill="width"` — and `w-full` is 100% of the host's CONTENT box,
 * which a negative margin then slides sideways rather than widens, bleeding on
 * one edge and falling short on the other. A plain Box takes the negative pair
 * (width auto resolves to the host's width MINUS its margins, so the pair
 * widens it, in a block host and a stretching flex column alike) and the button
 * fills that. `WidgetListRow` needs no wrapper because it is already a div.
 *
 * It builds on {@link WidgetInteractive}, so a press acts on the row even
 * inside a `bodyDraggable` card. The host module leaves `bleed` off — see
 * {@link WidgetListRow} for why a bleeding body cuts the wash at the card's
 * border.
 */
export type WidgetDisclosureRowProps = Omittable<{
  /** Whether this row's panel is the open one. */
  expanded: boolean;
  onToggle: () => void;
  /**
   * What the row opens, for the button's name — "Expand Robert Leng". The verb
   * is the row's so that every disclosure in the system announces itself with
   * the same one.
   */
  label: string;
  children: ReactNode;
}>;

export function WidgetDisclosureRow({
  expanded,
  onToggle,
  label,
  children,
}: WidgetDisclosureRowProps) {
  return (
    <Box marginXNegative={ROW_INSET}>
      <WidgetInteractive
        as="button"
        type="button"
        direction="row"
        align="center"
        gap={ROW_GAP}
        fill="width"
        paddingX={ROW_INSET}
        paddingY={ROW_INSET}
        rounded="sm"
        cursor="pointer"
        hoverBackground
        background={expanded ? "selected" : undefined}
        // The row owns the `row` scope, exactly as `WidgetListRow` does: per-row
        // chrome reveals with `hoverReveal="row"` and the enclosing card keeps
        // `box`, so a hover here never lights every sibling row.
        group="row"
        aria-expanded={expanded}
        aria-label={`${expanded ? "Collapse" : "Expand"} ${label}`}
        onClick={onToggle}
      >
        <Icon
          name={expanded ? "caret-down" : "caret-right"}
          size="xs"
          color="muted"
        />
        {children}
      </WidgetInteractive>
    </Box>
  );
}

export type WidgetDisclosureRowSkeletonProps = Omittable<{
  /**
   * Keep the open row's wash on this placeholder. A panel that stays open
   * beneath a list while the list re-queries needs the row it belongs to still
   * marked, or it hangs under a run of anonymous placeholders.
   */
  selected?: boolean;
  /** The caller's placeholders for its own content — avatar, name, figures —
   *  laid out exactly as the real row lays its children out. */
  children: ReactNode;
}>;

/**
 * The placeholder twin of {@link WidgetDisclosureRow}: the same bleed, inset,
 * gap and caret slot, so a list of these is the exact height of the list
 * that replaces it and the avatars land on the same edge. It is NOT a button —
 * a placeholder discloses nothing, so it offers no press, no name and no
 * caret; the caret's box is held open and left empty. The row content is
 * the caller's because only the caller knows what its rows hold; the shell is
 * the library's because that is what has to match. Reports itself as a loading
 * body to the measuring card while mounted, like every placeholder.
 */
export function WidgetDisclosureRowSkeleton({
  selected = false,
  children,
}: WidgetDisclosureRowSkeletonProps) {
  useWidgetBodyLoadingWhile(true);
  return (
    <Box marginXNegative={ROW_INSET} aria-hidden>
      <Box
        direction="row"
        align="center"
        gap={ROW_GAP}
        fill="width"
        paddingX={ROW_INSET}
        paddingY={ROW_INSET}
        rounded="sm"
        background={selected ? "selected" : undefined}
      >
        <Box width={CARET_PX} height={CARET_PX} shrink={false} />
        {children}
      </Box>
    </Box>
  );
}
